import { useMutation } from '@apollo/client';
import { UpdateOrganisationWebhookDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { UpdateWebhookInput } from './useUpdateWebhookTRPC';
import { useUpdateWebhookTRPC } from './useUpdateWebhookTRPC';

export const useUpdateWebhook = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [updateWebhookGraphQL, graphqlState] = useMutation(
    UpdateOrganisationWebhookDocument,
    {
      update: (cache) => {
        evictField(cache, 'organisation_webhook');
      },
    }
  );

  const trpcMutation = useUpdateWebhookTRPC();

  const updateWebhook = async (input: UpdateWebhookInput): Promise<void> => {
    if (trpcEnabled) {
      await trpcMutation.updateWebhook(input);

      return;
    }

    const result = await updateWebhookGraphQL({
      variables: {
        object: {
          Id: input.id,
          Title: input.Title,
          Description: input.Description,
          WebhookUrl: input.WebhookUrl,
          WebhookApiVersion: input.WebhookApiVersion,
          Status: input.Status,
          WebhookEventTypeIds: input.WebhookEventTypeIds,
        },
      },
    });
    if (!result.data) {
      throw new Error('Failed to update webhook');
    }
  };

  if (trpcEnabled) {
    return {
      updateWebhook,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateWebhook,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
